import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import ReactMapboxGl, { Popup } from "react-mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTimesCircle as farTimesCircle } from "@fortawesome/free-solid-svg-icons";
import mapboxgl from "mapbox-gl";
mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

import hexToRGB from "../helpers/hexToRGB";
import { getLocationTypeString } from "../helpers/location";

import LocationsContainer from "../containers/LocationsContainer";
import DirectionsContainer from "../containers/DirectionsContainer";

const Map = ReactMapboxGl({
  accessToken:
    "pk.eyJ1IjoidGhlcHJvZiIsImEiOiJjazRxaTFpMDUwMXd6M21uMHRvYXBlanY1In0.7v5Itqbe2GQvWxtkefhcug",
});

library.add(farTimesCircle);

const Table = styled.table`
  border: none;
  border-collapse: collapse;
  font-family: Lato;
  font-size: 12px;
  color: #b8c4c9;
  font-weight: 400;
  tbody {
    tr > td {
      padding: 4px 8px;
    }
    tr > td:last-child {
      color: #56666c;
      min-width: 200px;
    }
    tr:nth-child(even) {
      border-radius: 2px;
      background-color: #eceff1;
    }
  }
`;

const TopTable = styled.table`
  margin: 10px 0;
  border: none;
  border-collapse: collapse;
  font-family: Lato;
  font-size: 12px;
  color: #b8c4c9;
  font-weight: 400;
  tbody {
    tr > td {
      padding: 3px 15px 3px 0;
    }
    tr > td:last-child {
      color: #56666c;
    }
  }
`;

const BottomTable = styled.table`
  border-collapse: collapse;
  margin-top: 5px;
  font-family: Lato;
  font-size: 12px;
  color: #5c5b5a;
  font-weight: 400;
  thead {
    tr {
      background-color: #56666c;
    }
    tr > th {
      text-align: left;
      font-family: Lato;
      font-weight: 700;
      color: white;
      padding: 10px;
      white-space: nowrap;
    }
  }
  tbody {
    tr > td {
      padding: 10px;
      white-space: nowrap;
    }
    tr > td > a {
      color: #599cf5;
      text-decoration: none;
    }
    tr:nth-child(even) {
      background-color: #f1f5f7;
    }
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: -10px;
  right: -15px;
  width: 60px;
  line-height: 60px;
  text-align: center;
  cursor: pointer;
`;

const IconContainer = styled.div`
  box-shadow: 0 1px 3px 0 rgba(86, 102, 108, 0.5);
  border-radius: 50%;
`;

const IconContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 7px;
  background-color: ${({ color }) =>
    hexToRGB(color, 0.8) || hexToRGB("gray", 0.8)};
  border: 1px solid ${({ color }) => color || "gray"};
  text-align: center;
  border-radius: 50%;
  color: white;
`;

const InlineBlock = styled.div`
  display: flex;
  align-items: center;

  > div {
    margin-right: 10px;
  }
`;

const Badge = styled.span`
  padding: 0.286em 0.5em;
  border: 0.071em solid ${({ color }) => color || "#C4C4C4"};
  border-radius: 0.143em;
  font-size: 0.643em;
  font-family: Lato;
  font-weight: 700;
  cursor: pointer;
  color: ${({ color }) => color || "#C4C4C4"};
  background-color: ${({ color }) =>
    hexToRGB(color, 0.2) || hexToRGB("#C4C4C4", 0.2)};
`;

const TableCell = styled.td`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const defaultCenter = [49.24718165841392, -123.10948578314043];

function MyMapBoxMap({
  locations,
  fleetIds,
  currentLocation,
  updateLocationState,
}) {
  const [center, setCenter] = useState({
    lat: defaultCenter[0],
    lng: defaultCenter[1],
  });

  useEffect(() => {
    handleSetCenter(currentLocation);
  }, [currentLocation]);

  function handleSetCenter(location) {
    if (location && location.lat) {
      setCenter({
        lat: location.lat,
        lng: location.lng,
      });
    }
  }

  function handleClosePopup() {
    updateLocationState(undefined);
  }

  function getLoad(location) {
    const { load } = location || {};

    if (typeof load === "number") {
      return load.toString();
    }

    if (typeof load === "object") {
      const res = [];

      _.forEach(_.keys(load), (key) => {
        res.push(`${key}: ${load[key]}`);
      });

      return res.join(", ");
    }

    return "";
  }

  function getDepotIcon(location) {
    return (
      <IconContainer>
        <IconContent color={`#${location.color}`}>
          {location.vehicleId !== "unserved" && (
            <FontAwesomeIcon icon={"truck"} flip="horizontal" />
          )}
          {location.vehicleId === "unserved" && (
            <FontAwesomeIcon icon={"times-circle"} style={{ fontSize: 12 }} />
          )}
        </IconContent>
      </IconContainer>
    );
  }

  function renderPopup(location) {
    if (location.isStartingLocation) {
      const locationList =
        locations.filter((l) => l.vehicleId === location.vehicleId) || [];
      const origin = locationList[0] || {};
      const destination = locationList.slice(-1)[0] || {};
      const breaks = location.breaks;

      return (
        <Popup
          coordinates={[location.lng, location.lat]}
          offset={{
            "bottom-left": [12, -38],
            bottom: [0, -38],
            "bottom-right": [-12, -38],
          }}
        >
          <div>
            <CloseButton onClick={handleClosePopup}>X</CloseButton>
            <InlineBlock>
              {getDepotIcon(location)}
              <Badge color={`#${location.color}`}>{location.vehicleId}</Badge>
            </InlineBlock>
            <TopTable>
              <tbody>
                <tr>
                  <td>Time Window</td>
                  <td>
                    {origin?.shift_start || "N/A"} -{" "}
                    {origin?.shift_end || "N/A"}
                  </td>
                </tr>
                {breaks.map((breakItem, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td>Break Time</td>
                      <td>
                        {breakItem.start} - {breakItem.end}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                <tr>
                  <td>Start Location</td>
                  <td>{origin.location_name || origin.address}</td>
                </tr>
                <tr>
                  <td>End Location</td>
                  <td>{destination.location_name || destination.address}</td>
                </tr>
                <tr>
                  <td>Arrival Time</td>
                  <td>{origin.arrival_time}</td>
                </tr>
                <tr>
                  <td>Finish Time</td>
                  <td>{destination.finish_time}</td>
                </tr>
                <tr>
                  <td>Skills</td>
                  <td>{getLocationTypeString(location)}</td>
                </tr>
              </tbody>
            </TopTable>
            <div style={{ height: 230, overflowY: "scroll" }}>
              <BottomTable>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Time Window</th>
                    <th width="60">Arrival</th>
                    <th width="60">Finish</th>
                    <th>Address</th>
                    <th>Load</th>
                    <th>Skills</th>
                  </tr>
                </thead>
                <tbody>
                  {locationList.map((l, i) => (
                    <tr key={i}>
                      <td>{l.location_id}</td>
                      <TableCell title={l.name}>{l.name}</TableCell>
                      <td style={{ textTransform: "capitalize" }}>{l.vType}</td>
                      {["start", "end"].includes(l.vType) && (
                        <td>
                          {l?.shift_start || "N/A"} - {l?.shift_end || "N/A"}
                        </td>
                      )}
                      {["pickup", "dropoff", "stop"].includes(l.vType) && (
                        <td>
                          {l?.start || "N/A"} - {l?.end || "N/A"}
                        </td>
                      )}
                      <td>{l.arrival_time}</td>
                      <td>{l.finish_time}</td>
                      <TableCell title={l.address || l.location_name || "#"}>
                        <a
                          href={`https://www.google.com/maps/place/${l.lat},${l.lng}`}
                          target="_blank"
                        >
                          {l.address || l.location_name || "#"}
                        </a>
                      </TableCell>
                      <td>{getLoad(l.load)}</td>
                      <td>{getLocationTypeString(l)}</td>
                    </tr>
                  ))}
                </tbody>
              </BottomTable>
            </div>
          </div>
        </Popup>
      );
    }

    const customNotes = [];
    _.forEach(_.keys(location.customNotes || {}), (label) => {
      const val = location.customNotes[label];
      if (!!val) {
        customNotes.push(`${label}: ${val}`);
        customNotes.push(<br />);
      }
    });

    return (
      <Popup
        coordinates={[location.lng, location.lat]}
        offset={{
          "bottom-left": [12, -38],
          bottom: [0, -38],
          "bottom-right": [-12, -38],
        }}
      >
        <div>
          <CloseButton onClick={handleClosePopup}>X</CloseButton>
          <InlineBlock style={{ paddingBottom: 15 }}>
            {getDepotIcon(location)}
            <Badge color={`#${location.color}`}>
              {_.compact([
                _.capitalize(location.vType),
                location.vehicleId,
              ]).join(" - ")}
            </Badge>
          </InlineBlock>
          <Table>
            <tbody>
              <tr>
                <td>Location ID:</td>
                <td>{location.location_id}</td>
              </tr>
              <tr>
                <td>Location Name:</td>
                <td>{location.name}</td>
              </tr>
              <tr>
                <td>Time Window:</td>
                <td>
                  {location?.start || "N/A"} - {location?.end || "N/A"}
                </td>
              </tr>
              <tr>
                <td>Arrival time:</td>
                <td>{location.arrival_time}</td>
              </tr>
              <tr>
                <td>Finish time:</td>
                <td>{location.finish_time}</td>
              </tr>
              <tr>
                <td>Skills:</td>
                <td>{getLocationTypeString(location)}</td>
              </tr>
              <tr>
                <td>Priority:</td>
                <td>{location.priority}</td>
              </tr>
              <tr>
                <td>Load:</td>
                <td>{getLoad(location)}</td>
              </tr>
              <tr>
                <td>Address:</td>
                <td>{location.address}</td>
              </tr>
              {customNotes.length > 0 && (
                <tr>
                  <td valign="top">Custom Notes:</td>
                  <td>{customNotes}</td>
                </tr>
              )}
              {location.unservedReason && (
                <tr>
                  <td>Unserved Reason:</td>
                  <td style={{ color: "red" }}>{location.unservedReason}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Popup>
    );
  }
  return (
    <Map
      container="map"
      center={center}
      containerStyle={{
        height: "100vh",
        width: "100vw",
      }}
      movingMethod="easeTo"
      // eslint-disable-next-line
      style="mapbox://styles/theprof/cknjui1zh0q4z17np7i8u0h0z"
    >
      <DirectionsContainer fleetIds={fleetIds} />
      <LocationsContainer
        fleetIds={fleetIds}
        locations={locations}
        setCenter={handleSetCenter}
        currentLocation={currentLocation}
      />
      {currentLocation && renderPopup(currentLocation)}
    </Map>
  );
}

MyMapBoxMap.propTypes = {
  currentLocation: PropTypes.object,
  locations: PropTypes.array.isRequired,
  fleetIds: PropTypes.array.isRequired,
  updateLocationState: PropTypes.func.isRequired,
};

export default MyMapBoxMap;
